import { useLocale } from 'locale';
import React, { useState } from 'react';
import { ListingProps, tabsIcons, TabType } from '..';
import * as Styled from './styled';
import ChevronIcon from 'assets/svg/arrow-down-collapsed.svg';
import { ViewPager } from 'components/view-pager';
import { AgentOfficeBulletinCard } from 'screens/AgentOfficePages/common/components/Card';
import { Section } from 'components/navigation/subheader/SectionsNavigation';
import { IAgentOfficeBulletin } from 'utils/entities';
import { Action, Direction, EventProps } from 'screens/AgentOfficePages/types';
import withEvents from 'analytics/withEvents';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { noop } from 'lodash';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';


type SectionState = Partial<Record<TabType, boolean>>;
interface BulletinSection {
  tab: TabType;
  bulletins: IAgentOfficeBulletin[];
}

export const MobileListingsBase: React.FC<ListingProps> = ({ bulletinsDict, onCarouselScroll = noop, pageDetails }) => {
  const tabsOrder = [ TabType.Sale, TabType.Rent, TabType.CommercialSale, TabType.CommercialRent ];
  const sections = React.useMemo(() => tabsOrder.map<BulletinSection>((tab) => bulletinsDict[tab] && bulletinsDict[tab].length ? { bulletins: bulletinsDict[tab], tab } : null).filter(Boolean), [ bulletinsDict ]);
  const [ sectionState, setSectionState ] = useState<SectionState>({ [sections[0].tab]: true });
  const { t } = useLocale();
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = isOfficePage ? 'office_carousel_scroll' : 'agent_carousel_scroll';
  const category = getAgentOfficeCategory(isOfficePage);
  const eventHandler = (direction: Direction, action: Action) => {
    onCarouselScroll({
      event,
      category,
      direction,
      action,
      pageDetails,
    });
  };

  const handleSwipeLeft = () => {
    eventHandler(Direction.Left, Action.Swipe);
  };

  const handleSwipeRight = () => {
    eventHandler(Direction.Right, Action.Swipe);
  };

  const handleSectionChange = (tab: TabType) => {
    setSectionState((prevState) => ({
      ...prevState,
      [tab]: prevState ? !prevState[tab] : true,
    }));
  };

  const count = React.useMemo(() => sections.reduce((acc, k) => acc + k.bulletins.length, 0), [ bulletinsDict ]);

  return (
    <div>
      <Styled.AreaTitleMobile>{t('officePage.subMenu.item', { item: Section.LISTINGS })} · <span>{count}</span></Styled.AreaTitleMobile>
      {sections.map((section: BulletinSection, i) => (
        <Styled.CollapsedSection key={i} onClick={() => handleSectionChange(section.tab)}>
          <Styled.TitleSection>
            {tabsIcons[section.tab]}
            <Styled.Title dangerouslySetInnerHTML={{ __html: t('officePage.listing.tabs.title', { tabType: section.tab, count: section.bulletins.length }) }} />
            <Styled.Toggle isOpen={sectionState && sectionState[section.tab]}><ChevronIcon /></Styled.Toggle>
          </Styled.TitleSection>
          {sectionState && sectionState[section.tab] ? (
            <Styled.ListingSection>
              <Styled.CarouselWrapper>
                <ViewPager
                  verticalAlign="flex-start"
                  align="left"
                  isRTL={true}
                  onSwipeLeft={handleSwipeLeft}
                  onSwipeRight={handleSwipeRight}
                >
                  {section.bulletins.map((l, idx) => <Styled.CardWrapper style={{ width: 280 }} key={idx}><AgentOfficeBulletinCard poi={l} /></Styled.CardWrapper>)}
                </ViewPager>
              </Styled.CarouselWrapper>
            </Styled.ListingSection>
          ) : null}
        </Styled.CollapsedSection>
      ))}
    </div>
  );
};

export const MobileListings = withEvents<ListingProps>(sendEvent => ({
  onCarouselScroll(props: EventProps) {
    sendEvent(props.event, props.category, {
      ...props.pageDetails,
      event: {
        carousel_type: 'active_listings',
        direction: props.direction,
        action: props.action,
      },
    });
  },
}))(MobileListingsBase);
